import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  getRiskDetail,
  schvalitRisk,
  skrytRisk,
  zrusitSchvaleniRisk,
} from "../lib/api.js";
import type { RiskProductDetail } from "../lib/types.js";
import { LoadingBlock } from "../ui/Spinner.js";
import { EmptyState } from "../ui/EmptyState.js";
import { Badge } from "../ui/Badge.js";
import { Button } from "../ui/Button.js";
import { Breadcrumbs } from "../ui/Breadcrumbs.js";
import { PackageIcon, AlertIcon } from "../lib/icons.js";
import { FotkaVyrobce } from "./MotorexPage.js";

/**
 * RISK RACING — detail produktu a ovládání eshopu.
 *
 * Vít 15. 8. 2026: *„jen budeme mít možnost tudy ten produkt schválit a vložit
 * se vším co má mít nebo skrýt z eshopu, tak to pro mě bude jednoduchý na
 * ovládání i z mobilu."*
 *
 * Dvě tlačítka a každé jinak:
 *   „Schválit na eshop"  → zapíše příznak, produkt zakládá ROBOT do 15 minut.
 *     ⛔⛔ Zakládání zůstává v `risk_zalozeni.py` (repo `ktm-vlasim`). Není to
 *     jedno volání — počítá cenu z klíče, bere český popis, slučuje fotky
 *     z obou zdrojů, přidává videa a metapole a publikuje na kanály. Dvě
 *     implementace = dvě různé ceny, na tom se 13. 8. spálil Motorex.
 *   „Skrýt z eshopu"     → katalog udělá hned, je to jedno pole (DRAFT).
 *
 * 🔑 Ceny jsou tu všechny tři (dodavatel · výrobce · podle klíče) i s marží —
 * ať se rozhoduje s čísly před sebou, ne naslepo.
 */
/** Typy produktů, které dodavatel vede po velikostech (viz `jeObleceni`). */
const OBLECENI = new Set([
  "Jerseys",
  "Gloves",
  "Pants",
  "T Shirts",
  "Rain Coats",
  "Jackets",
  "Body Warmers",
  "Clothing Accessories",
]);

export function RiskDetailPage(): JSX.Element {
  const { sku } = useParams<{ sku: string }>();
  const [data, setData] = useState<RiskProductDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aktivniFoto, setAktivniFoto] = useState<string | null>(null);
  const [pracuje, setPracuje] = useState<string | null>(null);
  const [hlaska, setHlaska] = useState<{ typ: "ok" | "chyba"; text: string } | null>(null);

  const nacti = useCallback(() => {
    if (!sku) return;
    setLoading(true);
    setError(null);
    getRiskDetail(sku)
      .then((d) => {
        setData(d);
        setAktivniFoto((f) => f ?? d.imgUrls[0] ?? null);
      })
      .catch((e) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [sku]);

  useEffect(() => {
    nacti();
  }, [nacti]);

  /** Společná obsluha tlačítek — po akci se detail vždy načte znovu ze serveru. */
  const akce = useCallback(
    async (jmeno: string, fn: () => Promise<void>, hotovo: string) => {
      if (!sku) return;
      setPracuje(jmeno);
      setHlaska(null);
      try {
        await fn();
        setHlaska({ typ: "ok", text: hotovo });
        nacti();
      } catch (e) {
        setHlaska({ typ: "chyba", text: popisChyby(e) });
      } finally {
        setPracuje(null);
      }
    },
    [sku, nacti],
  );

  if (loading && !data) return <LoadingBlock label="Načítám produkt…" />;
  if (error || !data) {
    return (
      <EmptyState
        icon={<PackageIcon size={28} />}
        title="Produkt nenalezen"
        description={error ?? `SKU ${sku} v katalogu není.`}
      />
    );
  }

  const naEshopu = data.eshopStav === "na-eshopu";
  const ceka = data.eshopStav === "ceka";
  const skryto = data.eshopStav === "skryto";
  const bezKarty = data.abraCode === null;
  /**
   * ⛔ Dodavatel vede oblečení PO VELIKOSTECH — každá velikost je samostatné SKU
   * (33 dresů = 6 střihů × velikosti). Zakládací robot umí jen jednu variantu,
   * takže by z jednoho schválení vzniklo tolik produktů, kolik je velikostí.
   */
  const jeObleceni = OBLECENI.has(data.productType ?? "");

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <div className="mb-5">
        <Breadcrumbs
          items={[
            { label: "RISK RACING", to: "/risk" },
            ...(data.productType
              ? [{ label: data.productType, to: `/risk?type=${encodeURIComponent(data.productType)}` }]
              : []),
            { label: data.name },
          ]}
        />
      </div>

      {data.status === "vanished" && (
        <Pruh tón="red">
          <strong>Zmizelo z feedu dodavatele.</strong> Naposledy vidět{" "}
          {new Date(data.lastSeenAt).toLocaleDateString("cs-CZ")} — může to znamenat,
          že to výrobce ruší. Než to pustíš na eshop, ověř, že se dá objednat.
        </Pruh>
      )}

      {data.foundedError && (
        <Pruh tón="red">
          <strong>Robot to zkusil založit a nepovedlo se.</strong>{" "}
          <code className="text-xs bg-red-100 px-1 rounded">{data.foundedError}</code>{" "}
          Položka zůstává ve frontě a robot to zkusí znovu do 15 minut.
        </Pruh>
      )}

      {/*
        ⚠ `min-w-0` NENÍ ozdoba. Pásek náhledů níž je `flex … overflow-x-auto`
        a bez tohohle jeho obsah roztáhl sloupec mřížky na 568 px místo 324 —
        stránka pak na mobilu přetékala o 228 px (změřeno 15. 8. 2026).
        Vodorovně rolovatelný prvek uvnitř mřížky tohle potřebuje vždycky.
      */}
      <div className="grid md:grid-cols-2 gap-8 [&>*]:min-w-0">
        {/* Fotky */}
        <div>
          <div className="aspect-square bg-zinc-50 rounded-2xl border border-zinc-200 overflow-hidden">
            <FotkaVyrobce
              src={aktivniFoto}
              alt={data.name}
              ikona={48}
              className="w-full h-full object-contain p-6"
            />
          </div>
          {data.imgUrls.length > 1 && (
            <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
              {data.imgUrls.slice(0, 12).map((src) => (
                <button
                  key={src}
                  type="button"
                  onClick={() => setAktivniFoto(src)}
                  className={`w-16 h-16 shrink-0 rounded-lg border overflow-hidden bg-white ${
                    aktivniFoto === src ? "border-ktm-orange" : "border-zinc-200"
                  }`}
                >
                  <img src={src} alt="" loading="lazy" className="w-full h-full object-contain p-1" />
                </button>
              ))}
            </div>
          )}
          {data.videoIds.length > 0 && (
            <div className="mt-3 grid gap-2">
              {data.videoIds.slice(0, 2).map((id) => (
                <a
                  key={id}
                  href={`https://www.youtube.com/watch?v=${id}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-sm text-ktm-orange hover:underline"
                >
                  Video od výrobce →
                </a>
              ))}
            </div>
          )}
        </div>

        {/* Údaje + ovládání */}
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <StavStitek stav={data.eshopStav} />
            <Badge tone={data.stocked ? "green" : "amber"}>
              {data.stocked ? "Karta v ABŘE" : "Bez karty v ABŘE"}
            </Badge>
            {!data.available && <Badge tone="neutral">Vyprodáno u dodavatele</Badge>}
          </div>

          <h1 className="mt-3 text-2xl font-semibold text-zinc-900">{data.name}</h1>

          <div className="mt-4 text-3xl font-semibold text-zinc-900">
            {kc(data.suggestedPrice)}
            <span className="ml-2 text-sm font-normal text-zinc-500">naše cena podle klíče</span>
          </div>

          {/* 🔑 Proč zrovna tahle cena — všechna tři čísla vedle sebe. */}
          {data.cenovyKlic && (
            <div className="mt-4 rounded-xl border border-zinc-200 bg-zinc-50 p-4">
              <div className="text-sm font-medium text-zinc-900">Z čeho ta cena vyšla</div>
              <dl className="mt-3 space-y-1.5 text-sm">
                <Radek popis="nákup u dodavatele (bez DPH)" hodnota={kc(data.cenovyKlic.nakup)} />
                <Radek popis="+ doprava 12 %" hodnota={kc(data.cenovyKlic.doprava)} />
                <Radek
                  popis="= nákladovka"
                  hodnota={kc(data.cenovyKlic.nakladovka)}
                  silne
                />
                <Radek
                  popis={`+ marže ${data.cenovyKlic.marzeProcent} %`}
                  hodnota={kc(data.cenovyKlic.marzeKc)}
                  tón="green"
                />
                <Radek popis="= naše cena bez DPH" hodnota={kc(data.cenovyKlic.cenaBezDph)} />
                <Radek popis="+ DPH 21 %" hodnota={kc(data.cenovyKlic.cenaSDph)} silne />
              </dl>
              <p className="mt-3 text-xs text-zinc-500">
                Výrobce prodává za {kc(data.manufacturerPrice)} — prodává přímo, bez dovozu do ČR.
                {data.manufacturerPrice != null && data.suggestedPrice != null && (
                  <>
                    {" "}
                    Jsme{" "}
                    <strong>
                      {data.suggestedPrice >= data.manufacturerPrice ? "+" : ""}
                      {Math.round(
                        ((data.suggestedPrice - data.manufacturerPrice) /
                          data.manufacturerPrice) *
                          100,
                      )}{" "}
                      %
                    </strong>{" "}
                    proti němu.
                  </>
                )}
              </p>
              <p className="mt-2 text-xs text-zinc-500">
                ⛔ Clo je nula — dodavatel je v Severním Irsku (EORI <code>XI…</code>), a to je pro
                zboží pořád celní území EU. Kdyby začal posílat z Anglie (<code>GB…</code>), clo
                se platit začne.
              </p>
            </div>
          )}

          {/* ── Ovládání eshopu ────────────────────────────────────────── */}
          <div className="mt-6 rounded-xl border border-zinc-200 bg-white p-4">
            <div className="text-sm font-medium text-zinc-900">Eshop</div>

            {jeObleceni && !naEshopu && (
              <p className="mt-2 text-xs text-red-700">
                ⛔ <strong>Oblečení dodavatel vede po velikostech</strong> — každá
                velikost má vlastní SKU. Zakládací robot umí jen jednu variantu, takže
                by z jednoho dresu vzniklo tolik produktů, kolik je velikostí. Než to
                robot umí slučovat, zakládej oblečení ručně.
              </p>
            )}

            {bezKarty && !naEshopu && (
              <p className="mt-2 text-xs text-amber-700">
                ⚠ Tenhle kus nemá kartu v ABŘE. Založí se se SKU{" "}
                <code className="font-mono">{data.eshopSku}</code> (kód výrobce) a{" "}
                <strong>do vzniku karty u něj nesedí sklad ani dostupnost</strong>. Až karta
                vznikne, přepíše se SKU přes <code>risk_sku_oprava.py</code>.
              </p>
            )}

            {ceka && (
              <p className="mt-2 text-xs text-sky-700">
                Schváleno {kdy(data.approvedAt)}
                {data.approvedBy ? ` (${data.approvedBy})` : ""} — robot zakládá každých 15 minut.
              </p>
            )}
            {naEshopu && (
              <p className="mt-2 text-xs text-emerald-700">
                Založeno {kdy(data.foundedAt)} pod SKU{" "}
                <code className="font-mono">{data.eshopSku}</code>.
              </p>
            )}
            {skryto && (
              <p className="mt-2 text-xs text-zinc-500">
                Skryto {kdy(data.hiddenAt)} — produkt v adminu zůstal, jen ho zákazník nevidí.
              </p>
            )}

            <div className="mt-3 flex gap-2 flex-wrap">
              {!naEshopu && !ceka && (
                <Button
                  onClick={() =>
                    akce("schvalit", () => schvalitRisk(data.sku), "Schváleno — robot to založí do 15 minut.")
                  }
                  disabled={pracuje !== null}
                >
                  {pracuje === "schvalit" ? "Schvaluji…" : "Schválit na eshop"}
                </Button>
              )}
              {ceka && (
                <Button
                  variant="secondary"
                  onClick={() =>
                    akce("zrusit", () => zrusitSchvaleniRisk(data.sku), "Schválení zrušeno.")
                  }
                  disabled={pracuje !== null}
                >
                  {pracuje === "zrusit" ? "Ruším…" : "Zrušit schválení"}
                </Button>
              )}
              {(naEshopu || data.shopifyProductId !== null) && !skryto && (
                <Button
                  variant="secondary"
                  onClick={() =>
                    akce("skryt", () => skrytRisk(data.sku), "Skryto — zákazník už produkt nevidí.")
                  }
                  disabled={pracuje !== null}
                >
                  {pracuje === "skryt" ? "Skrývám…" : "Skrýt z eshopu"}
                </Button>
              )}
              {skryto && (
                <Button
                  onClick={() =>
                    akce("schvalit", () => schvalitRisk(data.sku), "Schváleno — robot produkt vrátí do 15 minut.")
                  }
                  disabled={pracuje !== null}
                >
                  {pracuje === "schvalit" ? "Vracím…" : "Vrátit na eshop"}
                </Button>
              )}
            </div>

            {hlaska && (
              <p
                className={`mt-3 text-sm ${
                  hlaska.typ === "ok" ? "text-emerald-700" : "text-red-700"
                }`}
              >
                {hlaska.text}
              </p>
            )}
          </div>

          <dl className="mt-6 grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
            <Pole label="SKU výrobce" hodnota={data.sku} mono />
            <Pole label="Kód v ABŘE" hodnota={data.abraCode ?? "—"} mono />
            <Pole label="SKU na eshopu" hodnota={data.eshopSku} mono />
            <Pole label="EAN" hodnota={data.ean ?? "—"} mono />
            <Pole label="Typ u výrobce" hodnota={data.productType ?? "—"} />
            <Pole
              label="Hmotnost"
              hodnota={data.weightGrams != null ? `${data.weightGrams} g` : "—"}
            />
            <Pole
              label="Dostupnost u dodavatele"
              hodnota={data.available ? "skladem" : "vyprodáno"}
            />
            <Pole
              label="Naposled ve feedu"
              hodnota={new Date(data.lastSeenAt).toLocaleDateString("cs-CZ")}
            />
          </dl>

          <p className="mt-3 text-xs text-zinc-400">
            ⚠ Počet kusů neznáme — velkoobchod ani výrobce ho nedávají, jen skladem/vyprodáno.
          </p>

          <div className="mt-4 flex gap-4 text-sm">
            {data.supplierUrl && (
              <a href={data.supplierUrl} target="_blank" rel="noreferrer"
                 className="text-ktm-orange hover:underline">
                Otevřít u velkoobchodu →
              </a>
            )}
            {data.manufacturerUrl && (
              <a href={data.manufacturerUrl} target="_blank" rel="noreferrer"
                 className="text-ktm-orange hover:underline">
                Otevřít u výrobce →
              </a>
            )}
          </div>
        </div>
      </div>

      {data.descriptionHtml && (
        <section className="mt-10">
          <h2 className="text-lg font-semibold text-zinc-900">
            Popis{" "}
            <span className="text-sm font-normal text-zinc-400">
              {data.descriptionSource === "vyrobce" ? "od výrobce" : "od dodavatele"}
            </span>
          </h2>
          <p className="mt-1 text-xs text-zinc-500">
            ⚠ Anglicky. Na eshop jde česká verze z <code>preklady.json</code>, tenhle text
            je originál od zdroje.
          </p>
          <div
            className="mt-3 prose prose-sm max-w-none text-zinc-700"
            // Popis je HTML od výrobce — stejně jako u MEFO, Motorexu a Garminu.
            dangerouslySetInnerHTML={{ __html: data.descriptionHtml }}
          />
        </section>
      )}
    </div>
  );
}

/** Chyby ze serveru mají strojové kódy — tady se z nich dělá česká věta. */
function popisChyby(e: unknown): string {
  const kod = e instanceof Error ? e.message : String(e);
  const slovnik: Record<string, string> = {
    uz_je_na_eshopu: "Produkt už na eshopu je.",
    na_eshopu_neni: "Na eshopu pod tímhle SKU nic není — není co skrývat.",
    robot_secret_not_configured:
      "Server nemá nastavené ROBOT_SECRET, takže by si robot frontu nevzal.",
    not_found: "Položka v katalogu není.",
    kolize_sku:
      "Kód výrobce se kryje s kódem naší karty v ABŘE u jiného produktu — " +
      "založit by to rozbilo sklad oběma. Nejdřív kartu, pak schválit.",
    shopify: "Shopify zápis odmítlo — podrobnosti jsou v logu serveru.",
  };
  return slovnik[kod] ?? `Nepovedlo se: ${kod}`;
}

function kc(v: number | null): string {
  return v != null ? `${v.toLocaleString("cs-CZ", { maximumFractionDigits: 0 })} Kč` : "—";
}

function kdy(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString("cs-CZ") : "—";
}

function StavStitek({ stav }: { stav: RiskProductDetail["eshopStav"] }): JSX.Element {
  switch (stav) {
    case "na-eshopu":
      return <Badge tone="green">Na eshopu</Badge>;
    case "ceka":
      return <Badge tone="blue">Čeká na robota</Badge>;
    case "chyba":
      return <Badge tone="red">Zakládání selhalo</Badge>;
    case "skryto":
      return <Badge tone="neutral">Skryto z eshopu</Badge>;
    default:
      return <Badge tone="amber">Na eshopu není</Badge>;
  }
}

function Pruh({ tón, children }: { tón: "red" | "amber"; children: React.ReactNode }): JSX.Element {
  const barvy =
    tón === "red"
      ? "border-red-200 bg-red-50 text-red-800"
      : "border-amber-200 bg-amber-50 text-amber-900";
  return (
    <div className={`mb-5 flex items-start gap-3 rounded-xl border px-4 py-3 ${barvy}`}>
      <AlertIcon size={18} className="mt-0.5 shrink-0" />
      <div className="text-sm">{children}</div>
    </div>
  );
}

function Radek({
  popis,
  hodnota,
  silne = false,
  tón,
}: {
  popis: string;
  hodnota: string;
  silne?: boolean;
  tón?: "green";
}): JSX.Element {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className={`text-zinc-500 ${silne ? "font-medium text-zinc-700" : ""}`}>{popis}</dt>
      <dd
        className={`tabular-nums ${silne ? "font-semibold text-zinc-900" : "text-zinc-700"} ${
          tón === "green" ? "text-emerald-700" : ""
        }`}
      >
        {hodnota}
      </dd>
    </div>
  );
}

function Pole({
  label,
  hodnota,
  mono = false,
}: {
  label: string;
  hodnota: string;
  mono?: boolean;
}): JSX.Element {
  return (
    <div>
      <dt className="text-xs text-zinc-500">{label}</dt>
      <dd className={`mt-0.5 text-zinc-900 ${mono ? "font-mono text-xs" : ""}`}>{hodnota}</dd>
    </div>
  );
}
